import { Scene } from '@babylonjs/core/scene';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Animation } from '@babylonjs/core/Animations/animation';
import { HighlightLayer } from '@babylonjs/core/Layers/highlightLayer';
import { GlowLayer } from '@babylonjs/core/Layers/glowLayer';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { EasingFunction, CircleEase, BounceEase, ElasticEase } from '@babylonjs/core/Animations/easing';
import { ParticleSystem } from '@babylonjs/core/Particles/particleSystem';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';

// 导入需要的副作用模块
import '@babylonjs/core/Animations/animatable';
import '@babylonjs/core/Meshes/Builders/boxBuilder';
import '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import '@babylonjs/core/Meshes/Builders/sphereBuilder';
import '@babylonjs/core/Meshes/Builders/torusBuilder';
import '@babylonjs/core/Meshes/Builders/planeBuilder';
import '@babylonjs/core/Meshes/Builders/polyhedronBuilder';
import '@babylonjs/core/Layers/effectLayerSceneComponent';

/**
 * 模型样式枚举 - 不同算法使用不同样式
 */
export type ModelStyle = 
  | 'cylinder'      // 圆柱+球体 (默认，冒泡/选择/插入排序)
  | 'hexagon'       // 六边形柱+菱形顶 (快速/归并排序)
  | 'pyramid'       // 金字塔形 (堆排序)
  | 'cube'          // 立方体+发光框 (搜索算法)
  | 'crystal'       // 晶体/八面体 (动态规划)
  | 'dataBlock'     // 数据块堆叠 (栈/队列)
  | 'node'          // 节点球体 (BFS/DFS)
  | 'ribbon'        // 带状卷轴 (字符串算法)
  | 'gear'          // 齿轮状 (数学运算)
  | 'prism'         // 多棱柱 (筛选算法)
  | 'star'          // 星形 (幂运算)
  | 'helix'         // 螺旋体 (A*寻路/贪心算法)
  | 'wave'          // 波浪体 (活动选择/扫描线)
  | 'vortex'        // 漩涡 (并查集/中心扩展)
  | 'tower';        // 塔状 (递归分治)

/**
 * 柱子元素信息
 */
interface BarElement {
  mesh: Mesh;           // 主体
  topSphere: Mesh;      // 顶部球体
  baseTorus: Mesh;      // 底座
  label: Mesh;
  value: number;
  originalX: number;
  extraMeshes?: Mesh[]; // 额外的网格（如漩涡光环、螺旋球体组、塔层等）
}

/**
 * 数组可视化器 - 将数组渲染为3D柱状图
 */
export class ArrayVisualizer {
  private scene: Scene;
  private highlightLayer: HighlightLayer;
  private glowLayer: GlowLayer;
  private bars: BarElement[] = [];
  private originalArray: number[] = [];
  private currentStyle: ModelStyle = 'cylinder';
  
  // 可视化参数
  private readonly barRadius = 0.35;
  private readonly baseSpacing = 1.4;  // 基础间距
  private readonly maxHeight = 5;
  private readonly sphereRadius = 0.25;
  
  // 颜色定义 - 柔和马卡龙色系
  private readonly colors = {
    default: new Color3(0.55, 0.75, 0.95),     // 天空蓝
    comparing: new Color3(1.0, 0.85, 0.4),     // 暖黄色
    swapping: new Color3(1.0, 0.6, 0.65),      // 珊瑚粉
    sorted: new Color3(0.55, 0.88, 0.7),       // 薄荷绿
    highlight: new Color3(1.0, 0.7, 0.5),      // 桃色
  };

  constructor(scene: Scene, highlightLayer: HighlightLayer) {
    this.scene = scene;
    this.highlightLayer = highlightLayer;
    
    // 创建发光层 - 降低强度
    this.glowLayer = new GlowLayer('glow', scene, {
      blurKernelSize: 32
    });
    this.glowLayer.intensity = 0.3;  // 降低发光强度
  }

  /**
   * 从数组创建可视化
   */
  async createFromArray(array: number[], style: ModelStyle = 'cylinder'): Promise<void> {
    this.clear();
    this.originalArray = [...array];
    this.currentStyle = style;
    
    // 防止除以零：当所有值都为0时，使用默认值1
    const maxValue = Math.max(...array) || 1;
    
    // 根据样式和数据计算动态间距
    const spacing = this.calculateSpacing(array, maxValue, style);
    const offset = (array.length - 1) * spacing / 2;
    
    for (let i = 0; i < array.length; i++) {
      const value = array[i];
      // 当值为0时，给一个最小高度以保证可见
      const normalizedHeight = value === 0 ? this.maxHeight * 0.15 : (value / maxValue) * this.maxHeight;
      const x = i * spacing - offset;
      
      // 根据样式创建不同形状的柱子
      const bar = this.createBarByStyle(value, normalizedHeight, x, i, style);
      this.bars.push(bar);
    }
    
    console.log(`✅ 已创建 ${this.bars.length} 个 ${style} 样式元素，间距: ${spacing.toFixed(2)}`);
  }

  /**
   * 根据样式和数据计算合适的间距
   */
  private calculateSpacing(array: number[], maxValue: number, style: ModelStyle): number {
    // 计算最大模型宽度
    let maxModelWidth = this.barRadius * 2;  // 默认宽度
    
    switch (style) {
      case 'crystal': {
        // 晶体样式：scale = max(0.4, height * 0.25)，实际直径约 scale * 2.2
        const maxHeight = this.maxHeight;
        const maxScale = Math.max(0.4, maxHeight * 0.25);
        maxModelWidth = maxScale * 2.5;  // 十二面体的实际宽度
        break;
      }
      case 'node': {
        // 节点样式：nodeSize = max(0.6, height * 0.3)，轨道环 = nodeSize * 2.8
        const maxHeight = this.maxHeight;
        const maxNodeSize = Math.max(0.6, maxHeight * 0.3);
        maxModelWidth = maxNodeSize * 2.8 + 0.3;  // 轨道环直径 + 小卫星偏移
        break;
      }
      case 'gear': {
        // 齿轮样式：diameter = height * 0.5 + 0.8
        const maxHeight = this.maxHeight;
        maxModelWidth = maxHeight * 0.5 + 0.8;
        break;
      }
      case 'pyramid': {
        // 金字塔：底部直径 = barRadius * 2.5
        maxModelWidth = this.barRadius * 2.5;
        break;
      }
      case 'hexagon':
      case 'prism': {
        maxModelWidth = this.barRadius * 2.2;
        break;
      }
      case 'star': {
        maxModelWidth = this.barRadius * 2.5;
        break;
      }
      case 'vortex': {
        // 漩涡样式：coreSize = max(0.35, height * 0.18)，最外层光环 = coreSize * 3.5
        const maxHeight = this.maxHeight;
        const maxCoreSize = Math.max(0.35, maxHeight * 0.18);
        maxModelWidth = maxCoreSize * 3.5;  // 最外层光环的直径
        break;
      }
      case 'helix': {
        // 螺旋体：螺旋半径 = barRadius * 1.2
        maxModelWidth = this.barRadius * 2.8;
        break;
      }
      case 'wave': {
        // 波浪体：波浪环直径 = barRadius * 3
        maxModelWidth = this.barRadius * 3.2;
        break;
      }
      case 'tower': {
        // 塔状：底座平台 = baseSize * 1.3
        maxModelWidth = this.barRadius * 3.5;
        break;
      }
      case 'cube': {
        // 立方体：size = max(0.6, height * 0.35)
        const maxHeight = this.maxHeight;
        const maxCubeSize = Math.max(0.6, maxHeight * 0.35);
        maxModelWidth = maxCubeSize * 1.5;  // 旋转后的对角线宽度
        break;
      }
      case 'dataBlock':
      case 'ribbon':
      default: {
        maxModelWidth = this.barRadius * 2.5;
        break;
      }
    }
    
    // 间距 = 模型宽度 + 最小间隙（0.3）
    const minGap = 0.3;
    const dynamicSpacing = maxModelWidth + minGap;
    
    // 返回动态间距，但不小于基础间距
    return Math.max(this.baseSpacing, dynamicSpacing);
  }

  /**
   * 根据样式创建不同形状
   */
  private createBarByStyle(value: number, height: number, x: number, index: number, style: ModelStyle): BarElement {
    switch (style) {
      case 'hexagon':
        return this.createHexagonBar(value, height, x, index);
      case 'pyramid':
        return this.createPyramidBar(value, height, x, index);
      case 'cube':
        return this.createCubeBar(value, height, x, index);
      case 'crystal':
        return this.createCrystalBar(value, height, x, index);
      case 'dataBlock':
        return this.createDataBlockBar(value, height, x, index);
      case 'node':
        return this.createNodeBar(value, height, x, index);
      case 'ribbon':
        return this.createRibbonBar(value, height, x, index);
      case 'gear':
        return this.createGearBar(value, height, x, index);
      case 'prism':
        return this.createPrismBar(value, height, x, index);
      case 'star':
        return this.createStarBar(value, height, x, index);
      case 'helix':
        return this.createHelixBar(value, height, x, index);
      case 'wave':
        return this.createWaveBar(value, height, x, index);
      case 'vortex':
        return this.createVortexBar(value, height, x, index);
      case 'tower':
        return this.createTowerBar(value, height, x, index);
      case 'cylinder':
      default:
        return this.createBar(value, height, x, index);
    }
  }

  /**
   * 创建单个柱子 - 圆柱体 + 顶部发光球 + 底座
   */
  private createBar(value: number, height: number, x: number, index: number): BarElement {
    // 创建底座圆环
    const baseTorus = MeshBuilder.CreateTorus(
      `base_${index}`,
      { diameter: this.barRadius * 3, thickness: 0.12, tessellation: 32 },
      this.scene
    );
    baseTorus.position = new Vector3(x, 0.06, 0);
    
    const baseMaterial = new StandardMaterial(`baseMat_${index}`, this.scene);
    baseMaterial.diffuseColor = this.colors.default.scale(0.6);
    baseMaterial.emissiveColor = this.colors.default.scale(0.2);
    baseMaterial.specularColor = new Color3(0.5, 0.5, 0.5);
    baseTorus.material = baseMaterial;
    
    // 创建主体圆柱
    const mesh = MeshBuilder.CreateCylinder(
      `bar_${index}`,
      { 
        height: height,
        diameter: this.barRadius * 2,
        tessellation: 24
      },
      this.scene
    );
    mesh.position = new Vector3(x, height / 2, 0);
    
    // 创建渐变材质
    const material = new StandardMaterial(`barMat_${index}`, this.scene);
    material.diffuseColor = this.colors.default;
    material.specularColor = new Color3(0.4, 0.4, 0.4);
    material.specularPower = 32;
    material.emissiveColor = this.colors.default.scale(0.15);
    mesh.material = material;
    
    // 创建顶部发光球
    const topSphere = MeshBuilder.CreateSphere(
      `sphere_${index}`,
      { diameter: this.sphereRadius * 2, segments: 16 },
      this.scene
    );
    topSphere.position = new Vector3(x, height + this.sphereRadius * 0.8, 0);
    
    const sphereMaterial = new StandardMaterial(`sphereMat_${index}`, this.scene);
    sphereMaterial.diffuseColor = this.colors.default;
    sphereMaterial.emissiveColor = this.colors.default.scale(0.5);
    sphereMaterial.specularColor = new Color3(1, 1, 1);
    sphereMaterial.specularPower = 64;
    topSphere.material = sphereMaterial;
    
    // 添加到发光层
    this.glowLayer.addIncludedOnlyMesh(mesh);
    this.glowLayer.addIncludedOnlyMesh(topSphere);
    this.glowLayer.addIncludedOnlyMesh(baseTorus);
    
    // 创建数值标签
    const label = this.createLabel(value.toString(), topSphere);
    
    return {
      mesh,
      topSphere,
      baseTorus,
      label,
      value,
      originalX: x
    };
  }

  /**
   * 创建六边形柱子 - 快速/归并排序用 (绿宝石风格)
   */
  private createHexagonBar(value: number, height: number, x: number, index: number): BarElement {
    // 主体: 发光六边形柱体
    const mesh = MeshBuilder.CreateCylinder(
      `hexBar_${index}`,
      { height: height, diameter: this.barRadius * 2.2, tessellation: 6 },
      this.scene
    );
    // 确保底部不穿入地面
    const yPos = Math.max(height / 2, 0.1);
    mesh.position = new Vector3(x, yPos, 0);
    const material = new StandardMaterial(`hexMat_${index}`, this.scene);
    material.diffuseColor = new Color3(0.2, 0.9, 0.5);  // 翡翠绿
    material.emissiveColor = new Color3(0.1, 0.5, 0.3);
    material.specularColor = new Color3(0.8, 1, 0.9);
    material.alpha = 0.85;
    mesh.material = material;
    
    // 顶部: 水平放置的六边形光环
    const topSphere = MeshBuilder.CreateTorus(
      `hexRing_${index}`,
      { diameter: this.barRadius * 2.5, thickness: 0.15, tessellation: 6 },
      this.scene
    );
    // 确保在柱体顶部上方
    const topY = Math.max(height + 0.2, yPos + height / 2 + 0.2);
    topSphere.position = new Vector3(x, topY, 0);
    // 不旋转，保持水平
    const ringMat = new StandardMaterial(`ringMat_${index}`, this.scene);
    ringMat.diffuseColor = new Color3(0.4, 1, 0.7);
    ringMat.emissiveColor = new Color3(0.2, 0.8, 0.5);
    topSphere.material = ringMat;
    
    // 底部光点 (轻量化)
    const baseTorus = MeshBuilder.CreateSphere(
      `hexGlow_${index}`,
      { diameter: 0.2, segments: 8 },
      this.scene
    );
    baseTorus.position = new Vector3(x, 0.1, 0);
    const glowMat = new StandardMaterial(`hexGlowMat_${index}`, this.scene);
    glowMat.diffuseColor = new Color3(0.3, 1, 0.6);
    glowMat.emissiveColor = new Color3(0.2, 0.8, 0.4);
    baseTorus.material = glowMat;
    
    this.glowLayer.addIncludedOnlyMesh(mesh);
    this.glowLayer.addIncludedOnlyMesh(topSphere);
    this.glowLayer.addIncludedOnlyMesh(baseTorus);
    
    const label = this.createLabel(value.toString(), topSphere);
    return { mesh, topSphere, baseTorus, label, value, originalX: x };
  }

  /**
   * 创建金字塔 - 堆排序用 (纯粹金字塔无底座)
   */
  private createPyramidBar(value: number, height: number, x: number, index: number): BarElement {
    // 主体: 四棱锥金字塔
    const mesh = MeshBuilder.CreateCylinder(
      `pyramid_${index}`,
      { height: height * 1.2, diameterTop: 0, diameterBottom: this.barRadius * 2.5, tessellation: 4 },
      this.scene
    );
    mesh.position = new Vector3(x, height * 0.6, 0);
    mesh.rotation.y = Math.PI / 4;
    const material = new StandardMaterial(`pyramidMat_${index}`, this.scene);
    material.diffuseColor = new Color3(1, 0.75, 0.2);  // 金色
    material.emissiveColor = new Color3(0.6, 0.4, 0.1);
    material.specularColor = new Color3(1, 0.9, 0.5);
    mesh.material = material;
    
    // 顶部: 发光的尖顶球
    const topSphere = MeshBuilder.CreateSphere(
      `pyramidTop_${index}`,
      { diameter: 0.35, segments: 12 },
      this.scene
    );
    topSphere.position = new Vector3(x, height * 1.2 + 0.2, 0);
    const topMat = new StandardMaterial(`pyramidTopMat_${index}`, this.scene);
    topMat.diffuseColor = new Color3(1, 0.95, 0.6);
    topMat.emissiveColor = new Color3(1, 0.8, 0.3);
    topSphere.material = topMat;
    
    // 底部投影光(模拟发光)
    const baseTorus = MeshBuilder.CreateDisc(
      `pyramidShadow_${index}`,
      { radius: this.barRadius * 1.5, tessellation: 4 },
      this.scene
    );
    baseTorus.position = new Vector3(x, 0.02, 0);
    baseTorus.rotation.x = Math.PI / 2;
    baseTorus.rotation.y = Math.PI / 4;
    const shadowMat = new StandardMaterial(`shadowMat_${index}`, this.scene);
    shadowMat.diffuseColor = new Color3(1, 0.6, 0.1);
    shadowMat.emissiveColor = new Color3(0.5, 0.3, 0.05);
    shadowMat.alpha = 0.4;
    baseTorus.material = shadowMat;
    
    this.glowLayer.addIncludedOnlyMesh(mesh);
    this.glowLayer.addIncludedOnlyMesh(topSphere);
    
    const label = this.createLabel(value.toString(), topSphere);
    return { mesh, topSphere, baseTorus, label, value, originalX: x };
  }

  /**
   * 创建立方体 - 搜索算法用 (纯立方体数据块)
   */
  private createCubeBar(value: number, height: number, x: number, index: number): BarElement {
    // 主体: 数据立方体
    const size = Math.max(0.6, height * 0.35);
    const mesh = MeshBuilder.CreateBox(
      `cube_${index}`,
      { width: size, height: size, depth: size },
      this.scene
    );
    mesh.position = new Vector3(x, size / 2 + (height - size) / 2, 0);
    mesh.rotation.y = Math.PI / 6;
    const material = new StandardMaterial(`cubeMat_${index}`, this.scene);
    material.diffuseColor = new Color3(0.3, 0.7, 1);  // 科技蓝
    material.emissiveColor = new Color3(0.15, 0.35, 0.5);
    material.specularColor = new Color3(0.8, 0.9, 1);
    mesh.material = material;
    
    // 顶部: 搜索指针箭头
    const topSphere = MeshBuilder.CreateCylinder(
      `pointer_${index}`,
      { height: 0.4, diameterTop: 0, diameterBottom: 0.3, tessellation: 3 },
      this.scene
    );
    topSphere.position = new Vector3(x, height + 0.3, 0);
    const pointerMat = new StandardMaterial(`pointerMat_${index}`, this.scene);
    pointerMat.diffuseColor = new Color3(1, 0.4, 0.4);  // 红色箭头
    pointerMat.emissiveColor = new Color3(0.6, 0.2, 0.2);
    topSphere.material = pointerMat;
    
    // 连接线 (Mock底座元素)
    const baseTorus = MeshBuilder.CreateCylinder(
      `connector_${index}`,
      { height: height - size, diameter: 0.08, tessellation: 8 },
      this.scene
    );
    baseTorus.position = new Vector3(x, (height - size) / 4, 0);
    const connMat = new StandardMaterial(`connMat_${index}`, this.scene);
    connMat.diffuseColor = new Color3(0.5, 0.8, 1);
    connMat.emissiveColor = new Color3(0.2, 0.4, 0.6);
    baseTorus.material = connMat;
    
    this.glowLayer.addIncludedOnlyMesh(mesh);
    this.glowLayer.addIncludedOnlyMesh(topSphere);
    
    const label = this.createLabel(value.toString(), mesh);
    return { mesh, topSphere, baseTorus, label, value, originalX: x };
  }

  /**
   * 创建晶体 - 动态规划用 (悬浮多面体)
   */
  private createCrystalBar(value: number, height: number, x: number, index: number): BarElement {
    // 主体: 旋转的十二面体
    const scale = Math.max(0.4, height * 0.25);
    const mesh = MeshBuilder.CreatePolyhedron(
      `crystal_${index}`,
      { type: 2, size: scale },  // 十二面体
      this.scene
    );
    // 确保底部不穿入地面: 中心位置至少为半径(scale)
    const yPos = Math.max(scale, height / 2);
    mesh.position = new Vector3(x, yPos, 0);
    mesh.rotation.x = Math.PI / 5;
    mesh.rotation.y = index * 0.5;
    const material = new StandardMaterial(`crystalMat_${index}`, this.scene);
    material.diffuseColor = new Color3(0.8, 0.4, 1);  // 紫色晶体
    material.emissiveColor = new Color3(0.4, 0.2, 0.6);
    material.specularColor = new Color3(1, 0.8, 1);
    material.alpha = 0.88;
    mesh.material = material;
    
    // 顶部: 小星星发光点
    const topSphere = MeshBuilder.CreatePolyhedron(
      `star_${index}`,
      { type: 1, size: 0.18 },  // 八面体
      this.scene
    );
    topSphere.position = new Vector3(x, height + 0.35, 0);
    topSphere.rotation.z = Math.PI / 4;
    const starMat = new StandardMaterial(`starMat_${index}`, this.scene);
    starMat.diffuseColor = new Color3(1, 0.8, 1);
    starMat.emissiveColor = new Color3(0.8, 0.5, 0.9);
    topSphere.material = starMat;
    
    // 能量光柱(模拟悬浮)
    const baseTorus = MeshBuilder.CreateCylinder(
      `beam_${index}`,
      { height: height * 0.4, diameter: 0.06, tessellation: 8 },
      this.scene
    );
    baseTorus.position = new Vector3(x, height * 0.2, 0);
    const beamMat = new StandardMaterial(`beamMat_${index}`, this.scene);
    beamMat.diffuseColor = new Color3(0.9, 0.6, 1);
    beamMat.emissiveColor = new Color3(0.6, 0.3, 0.8);
    beamMat.alpha = 0.5;
    baseTorus.material = beamMat;
    
    this.glowLayer.addIncludedOnlyMesh(mesh);
    this.glowLayer.addIncludedOnlyMesh(topSphere);
    
    // 标签挂载到顶部星星上，避免被晶体遮挡
    const label = this.createLabel(value.toString(), topSphere);
    return { mesh, topSphere, baseTorus, label, value, originalX: x };
  }

  /**
   * 创建数据块 - 栈/队列用 (纯方块堆叠，无底座)
   */
  private createDataBlockBar(value: number, height: number, x: number, index: number): BarElement {
    // 主体: 堆叠方块
    const mesh = MeshBuilder.CreateBox(
      `dataBlock_${index}`,
      { width: this.barRadius * 2.2, height: height * 0.9, depth: this.barRadius * 1.8 },
      this.scene
    );
    mesh.position = new Vector3(x, height * 0.45, 0);
    const material = new StandardMaterial(`dataMat_${index}`, this.scene);
    material.diffuseColor = new Color3(0.3, 0.5, 0.8);  // 钢蓝色
    material.emissiveColor = new Color3(0.15, 0.25, 0.4);
    material.specularColor = new Color3(0.7, 0.8, 1);
    mesh.material = material;
    
    // 顶部: 数据端口指示器
    const topSphere = MeshBuilder.CreateBox(
      `dataTop_${index}`,
      { width: this.barRadius * 2.4, height: 0.2, depth: this.barRadius * 2 },
      this.scene
    );
    topSphere.position = new Vector3(x, height * 0.9 + 0.15, 0);
    const topMat = new StandardMaterial(`dataTopMat_${index}`, this.scene);
    topMat.diffuseColor = new Color3(0.5, 0.8, 1);
    topMat.emissiveColor = new Color3(0.3, 0.5, 0.7);
    topSphere.material = topMat;
    
    // 连接槽位线
    const baseTorus = MeshBuilder.CreateBox(
      `dataSlot_${index}`,
      { width: 0.08, height: height * 0.6, depth: this.barRadius * 1.9 },
      this.scene
    );
    baseTorus.position = new Vector3(x - this.barRadius * 1.2, height * 0.35, 0);
    const slotMat = new StandardMaterial(`slotMat_${index}`, this.scene);
    slotMat.diffuseColor = new Color3(0.2, 0.4, 0.7);
    slotMat.emissiveColor = new Color3(0.1, 0.2, 0.35);
    baseTorus.material = slotMat;
    
    this.glowLayer.addIncludedOnlyMesh(mesh);
    this.glowLayer.addIncludedOnlyMesh(topSphere);
    
    const label = this.createLabel(value.toString(), mesh);
    return { mesh, topSphere, baseTorus, label, value, originalX: x };
  }

  /**
   * 创建节点球体 - BFS/DFS用 (纯球体悬浮风格)
   */
  private createNodeBar(value: number, height: number, x: number, index: number): BarElement {
    // 主体: 悬浮大球体
    const nodeSize = Math.max(0.6, height * 0.3);
    const mesh = MeshBuilder.CreateSphere(
      `node_${index}`,
      { diameter: nodeSize * 2, segments: 24 },
      this.scene
    );
    // 确保底部不穿入地面: 中心位置至少为半径(nodeSize)+小偏移
    const yPos = Math.max(nodeSize + 0.1, height / 2 + 0.3);
    mesh.position = new Vector3(x, yPos, 0);
    const material = new StandardMaterial(`nodeMat_${index}`, this.scene);
    material.diffuseColor = new Color3(1, 0.45, 0.25);  // 火焰橙
    material.emissiveColor = new Color3(0.6, 0.25, 0.12);
    material.specularColor = new Color3(1, 0.8, 0.6);
    mesh.material = material;
    
    // 轨道光环
    const topSphere = MeshBuilder.CreateTorus(
      `orbit_${index}`,
      { diameter: nodeSize * 2.8, thickness: 0.08, tessellation: 32 },
      this.scene
    );
    topSphere.position = new Vector3(x, yPos, 0);
    topSphere.rotation.x = Math.PI / 3;
    topSphere.rotation.z = index * 0.4;
    const orbitMat = new StandardMaterial(`orbitMat_${index}`, this.scene);
    orbitMat.diffuseColor = new Color3(1, 0.6, 0.3);
    orbitMat.emissiveColor = new Color3(0.6, 0.35, 0.15);
    orbitMat.alpha = 0.7;
    topSphere.material = orbitMat;
    
    // 小卫星球
    const baseTorus = MeshBuilder.CreateSphere(
      `satellite_${index}`,
      { diameter: 0.25, segments: 12 },
      this.scene
    );
    const angle = index * 1.2;
    baseTorus.position = new Vector3(
      x + Math.cos(angle) * nodeSize * 1.3,
      yPos + Math.sin(angle) * nodeSize * 0.8,
      Math.sin(angle) * nodeSize * 0.6
    );
    const satMat = new StandardMaterial(`satMat_${index}`, this.scene);
    satMat.diffuseColor = new Color3(1, 0.8, 0.4);
    satMat.emissiveColor = new Color3(0.7, 0.5, 0.2);
    baseTorus.material = satMat;
    
    this.glowLayer.addIncludedOnlyMesh(mesh);
    this.glowLayer.addIncludedOnlyMesh(topSphere);
    this.glowLayer.addIncludedOnlyMesh(baseTorus);
    
    const label = this.createLabel(value.toString(), mesh);
    return { mesh, topSphere, baseTorus, label, value, originalX: x };
  }

  /**
   * 创建带状卷轴 - 字符串算法用 (卷纸风格)
   */
  private createRibbonBar(value: number, height: number, x: number, index: number): BarElement {
    // 主体: 竖立的纸卷/卷轴
    const mesh = MeshBuilder.CreateCylinder(
      `ribbon_${index}`,
      { height: height * 0.8, diameterTop: this.barRadius * 1.6, diameterBottom: this.barRadius * 2, tessellation: 24 },
      this.scene
    );
    mesh.position = new Vector3(x, height * 0.4, 0);
    const material = new StandardMaterial(`ribbonMat_${index}`, this.scene);
    material.diffuseColor = new Color3(0.95, 0.9, 0.75);  // 羊皮纸色
    material.emissiveColor = new Color3(0.4, 0.35, 0.25);
    material.specularColor = new Color3(0.6, 0.5, 0.4);
    mesh.material = material;
    
    // 卷轴头
    const topSphere = MeshBuilder.CreateCylinder(
      `scroll_${index}`,
      { height: 0.15, diameter: this.barRadius * 2.4, tessellation: 24 },
      this.scene
    );
    topSphere.position = new Vector3(x, height * 0.8 + 0.1, 0);
    const scrollMat = new StandardMaterial(`scrollMat_${index}`, this.scene);
    scrollMat.diffuseColor = new Color3(0.6, 0.4, 0.25);  // 木色
    scrollMat.emissiveColor = new Color3(0.3, 0.2, 0.1);
    topSphere.material = scrollMat;
    
    // 卷轴底部
    const baseTorus = MeshBuilder.CreateCylinder(
      `scrollBase_${index}`,
      { height: 0.15, diameter: this.barRadius * 2.4, tessellation: 24 },
      this.scene
    );
    baseTorus.position = new Vector3(x, 0.075, 0);
    baseTorus.material = scrollMat;
    
    this.glowLayer.addIncludedOnlyMesh(mesh);
    
    // 标签挂载到卷轴头上，避免被主体遮挡
    const label = this.createLabel(value.toString(), topSphere);
    return { mesh, topSphere, baseTorus, label, value, originalX: x };
  }

  /**
   * 创建齿轮状 - 数学运算用 (机械风)
   */
  private createGearBar(value: number, height: number, x: number, index: number): BarElement {
    // 主体: 齿轮圆盘
    const mesh = MeshBuilder.CreateCylinder(
      `gear_${index}`,
      { height: 0.4, diameter: height * 0.5 + 0.8, tessellation: 12 },
      this.scene
    );
    mesh.position = new Vector3(x, height / 2, 0);
    mesh.rotation.x = Math.PI / 2;
    const material = new StandardMaterial(`gearMat_${index}`, this.scene);
    material.diffuseColor = new Color3(0.7, 0.75, 0.8);  // 银色金属
    material.emissiveColor = new Color3(0.2, 0.22, 0.25);
    material.specularColor = new Color3(0.9, 0.9, 1);
    mesh.material = material;
    
    // 中心轴
    const topSphere = MeshBuilder.CreateCylinder(
      `axle_${index}`,
      { height: height, diameter: 0.25, tessellation: 12 },
      this.scene
    );
    topSphere.position = new Vector3(x, height / 2, 0);
    const axleMat = new StandardMaterial(`axleMat_${index}`, this.scene);
    axleMat.diffuseColor = new Color3(0.5, 0.5, 0.55);
    axleMat.emissiveColor = new Color3(0.15, 0.15, 0.18);
    topSphere.material = axleMat;
    
    // 小齿轮装饰
    const baseTorus = MeshBuilder.CreateTorus(
      `gearRing_${index}`,
      { diameter: height * 0.4, thickness: 0.12, tessellation: 8 },
      this.scene
    );
    baseTorus.position = new Vector3(x, height / 2, 0);
    baseTorus.rotation.x = Math.PI / 2;
    const ringMat = new StandardMaterial(`gearRingMat_${index}`, this.scene);
    ringMat.diffuseColor = new Color3(0.85, 0.6, 0.2);  // 铜色
    ringMat.emissiveColor = new Color3(0.4, 0.3, 0.1);
    baseTorus.material = ringMat;
    
    this.glowLayer.addIncludedOnlyMesh(mesh);
    this.glowLayer.addIncludedOnlyMesh(baseTorus);
    
    const label = this.createLabel(value.toString(), mesh);
    return { mesh, topSphere, baseTorus, label, value, originalX: x };
  }

  /**
   * 创建多棱柱 - 筛选算法用 (晶体棱柱)
   */
  private createPrismBar(value: number, height: number, x: number, index: number): BarElement {
    // 主体: 八棱柱
    const mesh = MeshBuilder.CreateCylinder(
      `prism_${index}`,
      { height: height, diameter: this.barRadius * 2.2, tessellation: 8 },
      this.scene
    );
    mesh.position = new Vector3(x, height / 2, 0);
    mesh.rotation.y = Math.PI / 8;
    const material = new StandardMaterial(`prismMat_${index}`, this.scene);
    material.diffuseColor = new Color3(0.4, 0.85, 0.9);  // 青色
    material.emissiveColor = new Color3(0.2, 0.4, 0.45);
    material.specularColor = new Color3(0.8, 1, 1);
    material.alpha = 0.9;
    mesh.material = material;
    
    // 顶部尖顶
    const topSphere = MeshBuilder.CreateCylinder(
      `prismTop_${index}`,
      { height: 0.4, diameterTop: 0, diameterBottom: this.barRadius * 2.2, tessellation: 8 },
      this.scene
    );
    topSphere.position = new Vector3(x, height + 0.2, 0);
    topSphere.rotation.y = Math.PI / 8;
    const topMat = new StandardMaterial(`prismTopMat_${index}`, this.scene);
    topMat.diffuseColor = new Color3(0.5, 0.95, 1);
    topMat.emissiveColor = new Color3(0.25, 0.5, 0.55);
    topSphere.material = topMat;
    
    // 底部反射光符
    const baseTorus = MeshBuilder.CreateDisc(
      `prismGlow_${index}`,
      { radius: this.barRadius * 1.5, tessellation: 8 },
      this.scene
    );
    baseTorus.position = new Vector3(x, 0.01, 0);
    baseTorus.rotation.x = Math.PI / 2;
    baseTorus.rotation.y = Math.PI / 8;
    const glowMat = new StandardMaterial(`prismGlowMat_${index}`, this.scene);
    glowMat.diffuseColor = new Color3(0.3, 0.8, 0.9);
    glowMat.emissiveColor = new Color3(0.2, 0.5, 0.6);
    glowMat.alpha = 0.5;
    baseTorus.material = glowMat;
    
    this.glowLayer.addIncludedOnlyMesh(mesh);
    this.glowLayer.addIncludedOnlyMesh(topSphere);
    
    // 标签挂载到顶部尖顶上，避免被主体遮挡
    const label = this.createLabel(value.toString(), topSphere);
    return { mesh, topSphere, baseTorus, label, value, originalX: x };
  }

  /**
   * 创建星形 - 幂运算用 (发光星星)
   */
  private createStarBar(value: number, height: number, x: number, index: number): BarElement {
    // 主体: 五角星形柱体
    const mesh = MeshBuilder.CreateCylinder(
      `star_${index}`,
      { height: height * 0.7, diameterTop: this.barRadius * 1.5, diameterBottom: this.barRadius * 2.5, tessellation: 5 },
      this.scene
    );
    mesh.position = new Vector3(x, height * 0.35, 0);
    mesh.rotation.y = Math.PI / 5 * index;
    const material = new StandardMaterial(`starMat_${index}`, this.scene);
    material.diffuseColor = new Color3(1, 0.85, 0.3);  // 金黄色
    material.emissiveColor = new Color3(0.6, 0.5, 0.15);
    material.specularColor = new Color3(1, 0.95, 0.7);
    mesh.material = material;
    
    // 顶部发光星
    const topSphere = MeshBuilder.CreatePolyhedron(
      `starTop_${index}`,
      { type: 1, size: 0.35 },  // 八面体作为星星
      this.scene
    );
    topSphere.position = new Vector3(x, height * 0.7 + 0.4, 0);
    topSphere.rotation.y = Math.PI / 4 * index;
    topSphere.rotation.z = Math.PI / 6;
    const topMat = new StandardMaterial(`starTopMat_${index}`, this.scene);
    topMat.diffuseColor = new Color3(1, 0.95, 0.6);
    topMat.emissiveColor = new Color3(0.9, 0.75, 0.3);
    topSphere.material = topMat;
    
    // 光芒效果
    const baseTorus = MeshBuilder.CreateTorus(
      `starGlow_${index}`,
      { diameter: this.barRadius * 3, thickness: 0.06, tessellation: 24 },
      this.scene
    );
    baseTorus.position = new Vector3(x, height * 0.7 + 0.4, 0);
    baseTorus.rotation.x = Math.PI / 2;
    const glowMat = new StandardMaterial(`starGlowMat_${index}`, this.scene);
    glowMat.diffuseColor = new Color3(1, 0.9, 0.5);
    glowMat.emissiveColor = new Color3(0.8, 0.7, 0.3);
    glowMat.alpha = 0.6;
    baseTorus.material = glowMat;
    
    this.glowLayer.addIncludedOnlyMesh(mesh);
    this.glowLayer.addIncludedOnlyMesh(topSphere);
    this.glowLayer.addIncludedOnlyMesh(baseTorus);
    
    const label = this.createLabel(value.toString(), topSphere);
    return { mesh, topSphere, baseTorus, label, value, originalX: x };
  }

  /**
   * 创建螺旋体 - A*寻路/贪心算法用 (DNA双螺旋结构)
   */
  private createHelixBar(value: number, height: number, x: number, index: number): BarElement {
    // 主体: 螺旋中心柱
    const mesh = MeshBuilder.CreateCylinder(
      `helixCore_${index}`,
      { height: height, diameter: this.barRadius * 0.6, tessellation: 16 },
      this.scene
    );
    mesh.position = new Vector3(x, height / 2, 0);
    const material = new StandardMaterial(`helixMat_${index}`, this.scene);
    material.diffuseColor = new Color3(0.2, 0.85, 0.95);  // 青色
    material.emissiveColor = new Color3(0.1, 0.5, 0.6);
    material.specularColor = new Color3(0.7, 1, 1);
    mesh.material = material;
    
    // 螺旋轨道球体组 - 使用相对坐标
    const helixGroup = new Mesh(`helixGroup_${index}`, this.scene);
    helixGroup.position = new Vector3(x, 0, 0);  // 父节点设置为实际X位置
    const spiralCount = Math.max(4, Math.floor(height * 2));
    const orbDiameter = 0.18;
    const baseY = orbDiameter / 2 + 0.1;  // 确保底部球不穿入地面
    for (let i = 0; i < spiralCount; i++) {
      const angle = (i / spiralCount) * Math.PI * 3;
      const y = baseY + (i / spiralCount) * (height - baseY);  // Y从基础高度开始
      const radius = this.barRadius * 1.2;
      
      // 双螺旋 - 第一条
      const orb1 = MeshBuilder.CreateSphere(
        `helixOrb1_${index}_${i}`,
        { diameter: 0.18, segments: 8 },
        this.scene
      );
      // 使用相对坐标（去掉x偏移）
      orb1.position = new Vector3(
        Math.cos(angle) * radius,
        y,
        Math.sin(angle) * radius
      );
      const orb1Mat = new StandardMaterial(`orb1Mat_${index}_${i}`, this.scene);
      orb1Mat.diffuseColor = new Color3(1, 0.4, 0.6);  // 粉红
      orb1Mat.emissiveColor = new Color3(0.6, 0.2, 0.3);
      orb1.material = orb1Mat;
      orb1.parent = helixGroup;
      this.glowLayer.addIncludedOnlyMesh(orb1);
      
      // 双螺旋 - 第二条 (相位差180°)
      const orb2 = MeshBuilder.CreateSphere(
        `helixOrb2_${index}_${i}`,
        { diameter: 0.18, segments: 8 },
        this.scene
      );
      // 使用相对坐标（去掉x偏移）
      orb2.position = new Vector3(
        Math.cos(angle + Math.PI) * radius,
        y,
        Math.sin(angle + Math.PI) * radius
      );
      const orb2Mat = new StandardMaterial(`orb2Mat_${index}_${i}`, this.scene);
      orb2Mat.diffuseColor = new Color3(0.4, 0.8, 1);  // 蓝色
      orb2Mat.emissiveColor = new Color3(0.2, 0.4, 0.6);
      orb2.material = orb2Mat;
      orb2.parent = helixGroup;
      this.glowLayer.addIncludedOnlyMesh(orb2);
    }
    
    // 顶部发光球
    const topSphere = MeshBuilder.CreateSphere(
      `helixTop_${index}`,
      { diameter: 0.4, segments: 16 },
      this.scene
    );
    topSphere.position = new Vector3(x, height + 0.25, 0);
    const topMat = new StandardMaterial(`helixTopMat_${index}`, this.scene);
    topMat.diffuseColor = new Color3(0.5, 1, 0.9);
    topMat.emissiveColor = new Color3(0.3, 0.7, 0.6);
    topSphere.material = topMat;
    
    // 底部节点 - 水平放置在地面上
    const baseThickness = 0.1;
    const baseTorus = MeshBuilder.CreateTorus(
      `helixBase_${index}`,
      { diameter: this.barRadius * 2.5, thickness: baseThickness, tessellation: 24 },
      this.scene
    );
    // 水平放置，贴在地面上
    baseTorus.position = new Vector3(x, baseThickness / 2 + 0.02, 0);
    // 不旋转，保持水平
    const baseMat = new StandardMaterial(`helixBaseMat_${index}`, this.scene);
    baseMat.diffuseColor = new Color3(0.3, 0.9, 0.8);
    baseMat.emissiveColor = new Color3(0.15, 0.5, 0.4);
    baseTorus.material = baseMat;
    
    this.glowLayer.addIncludedOnlyMesh(mesh);
    this.glowLayer.addIncludedOnlyMesh(topSphere);
    this.glowLayer.addIncludedOnlyMesh(baseTorus);
    
    const label = this.createLabel(value.toString(), topSphere);
    return { mesh, topSphere, baseTorus, label, value, originalX: x, extraMeshes: [helixGroup] };
  }

  /**
   * 创建波浪体 - 活动选择/扫描线算法用 (起伏波形)
   */
  private createWaveBar(value: number, height: number, x: number, index: number): BarElement {
    // 主体: 波浪状柱体
    const mesh = MeshBuilder.CreateCylinder(
      `wave_${index}`,
      { 
        height: height, 
        diameterTop: this.barRadius * (1.5 + 0.3 * Math.sin(index * 0.8)),
        diameterBottom: this.barRadius * (2 - 0.3 * Math.sin(index * 0.8)),
        tessellation: 24 
      },
      this.scene
    );
    mesh.position = new Vector3(x, height / 2, 0);
    const material = new StandardMaterial(`waveMat_${index}`, this.scene);
    material.diffuseColor = new Color3(0.2, 0.6, 0.95);  // 海洋蓝
    material.emissiveColor = new Color3(0.1, 0.35, 0.6);
    material.specularColor = new Color3(0.6, 0.85, 1);
    material.alpha = 0.9;
    mesh.material = material;
    
    // 波浪纹理环 - 水平放置
    const ring1Y = Math.max(0.15, height * 0.3);
    const waveRing1 = MeshBuilder.CreateTorus(
      `waveRing1_${index}`,
      { diameter: this.barRadius * 2.5, thickness: 0.08, tessellation: 32 },
      this.scene
    );
    waveRing1.position = new Vector3(x, ring1Y, 0);
    // 不旋转，保持水平
    const ring1Mat = new StandardMaterial(`ring1Mat_${index}`, this.scene);
    ring1Mat.diffuseColor = new Color3(0.4, 0.8, 1);
    ring1Mat.emissiveColor = new Color3(0.2, 0.5, 0.7);
    ring1Mat.alpha = 0.7;
    waveRing1.material = ring1Mat;
    
    const ring2Y = Math.max(0.3, height * 0.6);
    const waveRing2 = MeshBuilder.CreateTorus(
      `waveRing2_${index}`,
      { diameter: this.barRadius * 2.2, thickness: 0.08, tessellation: 32 },
      this.scene
    );
    waveRing2.position = new Vector3(x, ring2Y, 0);
    // 不旋转，保持水平
    waveRing2.material = ring1Mat;
    
    // 顶部水珠
    const topSphere = MeshBuilder.CreateSphere(
      `waveDrop_${index}`,
      { diameter: 0.35, segments: 16 },
      this.scene
    );
    topSphere.position = new Vector3(x, height + 0.3, 0);
    const dropMat = new StandardMaterial(`dropMat_${index}`, this.scene);
    dropMat.diffuseColor = new Color3(0.5, 0.9, 1);
    dropMat.emissiveColor = new Color3(0.3, 0.6, 0.8);
    dropMat.alpha = 0.85;
    topSphere.material = dropMat;
    
    // 底部浪花 - 水平放置
    const baseThickness = 0.12;
    const baseTorus = MeshBuilder.CreateTorus(
      `waveBase_${index}`,
      { diameter: this.barRadius * 3, thickness: baseThickness, tessellation: 32 },
      this.scene
    );
    baseTorus.position = new Vector3(x, baseThickness / 2 + 0.02, 0);
    // 不旋转，保持水平
    const baseMat = new StandardMaterial(`waveBaseMat_${index}`, this.scene);
    baseMat.diffuseColor = new Color3(0.3, 0.7, 1);
    baseMat.emissiveColor = new Color3(0.15, 0.4, 0.6);
    baseMat.alpha = 0.6;
    baseTorus.material = baseMat;
    
    this.glowLayer.addIncludedOnlyMesh(mesh);
    this.glowLayer.addIncludedOnlyMesh(topSphere);
    this.glowLayer.addIncludedOnlyMesh(waveRing1);
    this.glowLayer.addIncludedOnlyMesh(waveRing2);
    this.glowLayer.addIncludedOnlyMesh(baseTorus);
    
    const label = this.createLabel(value.toString(), topSphere);
    return { mesh, topSphere, baseTorus, label, value, originalX: x, extraMeshes: [waveRing1, waveRing2] };
  }

  /**
   * 创建漩涡 - 并查集/中心扩展算法用 (旋转聚合效果)
   */
  private createVortexBar(value: number, height: number, x: number, index: number): BarElement {
    // 主体: 漩涡中心球 - 缩小尺寸防止重叠
    const coreSize = Math.max(0.35, height * 0.18);
    const mesh = MeshBuilder.CreateSphere(
      `vortexCore_${index}`,
      { diameter: coreSize * 2, segments: 24 },
      this.scene
    );
    // 最大漩涡环的半径（用于计算Y位置）
    const maxRingRadius = coreSize * (2.0 + 2 * 0.5) / 2;  // 最大环直径/2
    // 确保底部不穿入地面: Y位置至少为最大环半径+偏移
    const yPos = Math.max(maxRingRadius + 0.15, height / 2);
    mesh.position = new Vector3(x, yPos, 0);
    const material = new StandardMaterial(`vortexMat_${index}`, this.scene);
    material.diffuseColor = new Color3(0.7, 0.3, 0.9);  // 紫色
    material.emissiveColor = new Color3(0.4, 0.15, 0.55);
    material.specularColor = new Color3(0.9, 0.7, 1);
    mesh.material = material;
    
    // 漩涡环 - 多层旋转，缩小尺寸
    const rings: Mesh[] = [];
    for (let i = 0; i < 3; i++) {
      const ring = MeshBuilder.CreateTorus(
        `vortexRing_${index}_${i}`,
        { 
          diameter: coreSize * (2.0 + i * 0.5),  // 缩小光环尺寸
          thickness: 0.05 + i * 0.015, 
          tessellation: 32 
        },
        this.scene
      );
      ring.position = new Vector3(x, yPos, 0);
      ring.rotation.x = Math.PI / 2 + i * 0.3;
      ring.rotation.z = i * Math.PI / 4;
      const ringMat = new StandardMaterial(`vortexRingMat_${index}_${i}`, this.scene);
      ringMat.diffuseColor = new Color3(0.8 - i * 0.1, 0.4 + i * 0.1, 1);
      ringMat.emissiveColor = new Color3(0.5 - i * 0.1, 0.2, 0.7);
      ringMat.alpha = 0.7 - i * 0.1;
      ring.material = ringMat;
      rings.push(ring);
      this.glowLayer.addIncludedOnlyMesh(ring);
    }
    
    // 顶部能量点 - 位置基于主体球
    const topSphere = MeshBuilder.CreatePolyhedron(
      `vortexTop_${index}`,
      { type: 1, size: 0.2 },
      this.scene
    );
    // 顶部菱形应在主体球的上方
    const topY = yPos + coreSize + 0.35;
    topSphere.position = new Vector3(x, topY, 0);
    topSphere.rotation.y = index * 0.6;
    const topMat = new StandardMaterial(`vortexTopMat_${index}`, this.scene);
    topMat.diffuseColor = new Color3(1, 0.6, 0.9);
    topMat.emissiveColor = new Color3(0.7, 0.3, 0.6);
    topSphere.material = topMat;
    
    // 底部能量圈 - 水平放置在地面上
    const baseRingDiameter = coreSize * 3.5;
    const baseRingThickness = 0.1;
    const baseTorus = MeshBuilder.CreateTorus(
      `vortexBase_${index}`,
      { diameter: baseRingDiameter, thickness: baseRingThickness, tessellation: 32 },
      this.scene
    );
    // 水平放置，贴在地面上
    baseTorus.position = new Vector3(x, baseRingThickness / 2 + 0.02, 0);
    // 不旋转，保持水平
    const baseMat = new StandardMaterial(`vortexBaseMat_${index}`, this.scene);
    baseMat.diffuseColor = new Color3(0.6, 0.2, 0.8);
    baseMat.emissiveColor = new Color3(0.35, 0.1, 0.5);
    baseMat.alpha = 0.5;
    baseTorus.material = baseMat;
    
    this.glowLayer.addIncludedOnlyMesh(mesh);
    this.glowLayer.addIncludedOnlyMesh(topSphere);
    this.glowLayer.addIncludedOnlyMesh(baseTorus);
    
    // 标签挂载到顶部菱形，确保始终可见
    const label = this.createLabel(value.toString(), topSphere);
    return { mesh, topSphere, baseTorus, label, value, originalX: x, extraMeshes: rings };
  }

  /**
   * 创建塔状 - 递归分治算法用 (层叠塔结构)
   */
  private createTowerBar(value: number, height: number, x: number, index: number): BarElement {
    // 塔层数
    const layers = Math.max(3, Math.min(6, Math.floor(height * 1.5)));
    const layerHeight = height / layers;
    
    // 主体: 最大层作为mesh
    const baseSize = this.barRadius * 2.5;
    const mesh = MeshBuilder.CreateBox(
      `towerBase_${index}`,
      { width: baseSize, height: layerHeight * 0.9, depth: baseSize },
      this.scene
    );
    mesh.position = new Vector3(x, layerHeight * 0.45, 0);
    const material = new StandardMaterial(`towerMat_${index}`, this.scene);
    material.diffuseColor = new Color3(0.85, 0.55, 0.25);  // 土黄色
    material.emissiveColor = new Color3(0.45, 0.3, 0.12);
    material.specularColor = new Color3(0.9, 0.7, 0.5);
    mesh.material = material;
    
    // 其他塔层 - 使用相对坐标
    const towerGroup = new Mesh(`towerGroup_${index}`, this.scene);
    towerGroup.position = new Vector3(x, 0, 0);  // 父节点设置为实际X位置
    for (let i = 1; i < layers; i++) {
      const shrinkFactor = 1 - (i / layers) * 0.6;
      const layerSize = baseSize * shrinkFactor;
      const layer = MeshBuilder.CreateBox(
        `towerLayer_${index}_${i}`,
        { width: layerSize, height: layerHeight * 0.85, depth: layerSize },
        this.scene
      );
      // 使用相对于父节点的坐标（x=0）
      layer.position = new Vector3(0, layerHeight * (i + 0.42), 0);
      layer.rotation.y = i * Math.PI / 12;
      
      const layerMat = new StandardMaterial(`layerMat_${index}_${i}`, this.scene);
      const hue = 0.08 + i * 0.03;
      layerMat.diffuseColor = new Color3(0.9 - i * 0.05, 0.6 + i * 0.05, 0.3);
      layerMat.emissiveColor = new Color3(0.5 - i * 0.05, 0.35, 0.15);
      layer.material = layerMat;
      layer.parent = towerGroup;
      this.glowLayer.addIncludedOnlyMesh(layer);
    }
    
    // 顶部尖塔
    const topSphere = MeshBuilder.CreateCylinder(
      `towerSpire_${index}`,
      { height: layerHeight, diameterTop: 0, diameterBottom: baseSize * 0.35, tessellation: 4 },
      this.scene
    );
    topSphere.position = new Vector3(x, height + layerHeight * 0.3, 0);
    topSphere.rotation.y = Math.PI / 4;
    const spireMat = new StandardMaterial(`spireMat_${index}`, this.scene);
    spireMat.diffuseColor = new Color3(1, 0.85, 0.5);
    spireMat.emissiveColor = new Color3(0.7, 0.55, 0.25);
    topSphere.material = spireMat;
    
    // 底座平台
    const baseTorus = MeshBuilder.CreateBox(
      `towerPlatform_${index}`,
      { width: baseSize * 1.3, height: 0.12, depth: baseSize * 1.3 },
      this.scene
    );
    baseTorus.position = new Vector3(x, 0.06, 0);
    const platformMat = new StandardMaterial(`platformMat_${index}`, this.scene);
    platformMat.diffuseColor = new Color3(0.6, 0.5, 0.4);
    platformMat.emissiveColor = new Color3(0.3, 0.25, 0.2);
    baseTorus.material = platformMat;
    
    this.glowLayer.addIncludedOnlyMesh(mesh);
    this.glowLayer.addIncludedOnlyMesh(topSphere);
    this.glowLayer.addIncludedOnlyMesh(baseTorus);
    
    const label = this.createLabel(value.toString(), topSphere);
    return { mesh, topSphere, baseTorus, label, value, originalX: x, extraMeshes: [towerGroup] };
  }

  /**
   * 创建数值标签
   */
  private createLabel(text: string, parent: Mesh): Mesh {
    const planeHeight = 0.5;
    const plane = MeshBuilder.CreatePlane(
      `label_${text}`,
      { width: planeHeight * 1.5, height: planeHeight },
      this.scene
    );
    
    // 创建动态纹理
    const texture = new DynamicTexture(
      `labelTex_${text}`,
      { width: 128, height: 64 },
      this.scene,
      false
    );
    
    texture.hasAlpha = true;
    const ctx = texture.getContext();
    ctx.clearRect(0, 0, 128, 64);
    texture.drawText(text, null, null, 'bold 36px Arial', 'white', 'transparent', true);
    
    const material = new StandardMaterial(`labelMat_${text}`, this.scene);
    material.diffuseTexture = texture;
    material.emissiveColor = new Color3(1, 1, 1);
    material.disableLighting = true;
    material.backFaceCulling = false;
    plane.material = material;
    
    // 定位在父节点上方
    plane.parent = parent;
    plane.position.y = 0.6;  // 固定在父节点上方0.6单位
    plane.billboardMode = Mesh.BILLBOARDMODE_ALL;
    
    return plane;
  }

  /**
   * 柱子入场动画
   */
  private animateBarIn(mesh: Mesh, targetHeight: number): Promise<void> {
    return new Promise(resolve => {
      const originalY = mesh.position.y;
      mesh.position.y = 10;
      mesh.scaling.y = 0.1;
      
      // 位置动画
      const posAnimation = new Animation(
        'barInPos',
        'position.y',
        60,
        Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CONSTANT
      );
      posAnimation.setKeys([
        { frame: 0, value: 10 },
        { frame: 20, value: originalY }
      ]);
      
      // 缩放动画
      const scaleAnimation = new Animation(
        'barInScale',
        'scaling.y',
        60,
        Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CONSTANT
      );
      scaleAnimation.setKeys([
        { frame: 0, value: 0.1 },
        { frame: 20, value: 1 }
      ]);
      
      mesh.animations = [posAnimation, scaleAnimation];
      this.scene.beginAnimation(mesh, 0, 20, false, 1, resolve);
    });
  }

  /**
   * 高亮指定元素
   */
  async highlight(indices: number[], duration = 500): Promise<void> {
    for (const idx of indices) {
      if (this.bars[idx]) {
        this.setBarColor(idx, this.colors.highlight);
        this.highlightLayer.addMesh(this.bars[idx].mesh, Color3.Yellow());
      }
    }
    await this.delay(duration);
  }

  /**
   * 取消高亮
   */
  async unhighlight(indices: number[]): Promise<void> {
    for (const idx of indices) {
      if (this.bars[idx]) {
        this.setBarColor(idx, this.colors.default);
        this.highlightLayer.removeMesh(this.bars[idx].mesh);
      }
    }
  }

  /**
   * 比较两个元素（高亮显示 + 脉冲动画）
   */
  async compare(idx1: number, idx2: number, duration = 500): Promise<void> {
    if (this.bars[idx1] && this.bars[idx2]) {
      // 设置颜色和发光
      this.setBarColor(idx1, this.colors.comparing);
      this.setBarColor(idx2, this.colors.comparing);
      this.highlightLayer.addMesh(this.bars[idx1].mesh, Color3.Yellow());
      this.highlightLayer.addMesh(this.bars[idx2].mesh, Color3.Yellow());
      
      // 添加脉冲缩放动画
      await Promise.all([
        this.animatePulse(this.bars[idx1].mesh, duration * 0.8),
        this.animatePulse(this.bars[idx2].mesh, duration * 0.8)
      ]);
    }
    await this.delay(duration * 0.2);
  }

  /**
   * 交换两个元素 - 超炉酷版
   */
  async swap(idx1: number, idx2: number, duration = 500): Promise<void> {
    const bar1 = this.bars[idx1];
    const bar2 = this.bars[idx2];
    
    if (!bar1 || !bar2) return;
    
    // 设置交换颜色 - 更强的发光
    this.setBarColor(idx1, this.colors.swapping);
    this.setBarColor(idx2, this.colors.swapping);
    this.highlightLayer.addMesh(bar1.mesh, new Color3(1, 0.3, 0.3));
    this.highlightLayer.addMesh(bar2.mesh, new Color3(1, 0.3, 0.3));
    this.highlightLayer.addMesh(bar1.topSphere, new Color3(1, 0.5, 0.5));
    this.highlightLayer.addMesh(bar2.topSphere, new Color3(1, 0.5, 0.5));
    
    // 增强发光强度
    this.glowLayer.intensity = 0.6;
    
    const pos1 = bar1.mesh.position.x;
    const pos2 = bar2.mesh.position.x;
    const distance = Math.abs(pos2 - pos1);
    
    // 使用更长的时长让动画更流畅
    const swapDuration = duration * 1.2;
    
    // 动画交换位置 - 所有组件一起移动
    await Promise.all([
      this.animateBarSwap(bar1, pos2, swapDuration, true, distance),
      this.animateBarSwap(bar2, pos1, swapDuration, false, distance)
    ]);
    
    // 着陆弹跳效果
    await Promise.all([
      this.animateLanding(bar1.mesh),
      this.animateLanding(bar2.mesh)
    ]);
    
    // 恢复发光强度
    this.glowLayer.intensity = 0.3;
    
    // 更新数组中的位置
    [this.bars[idx1], this.bars[idx2]] = [this.bars[idx2], this.bars[idx1]];
    
    // 恢复颜色
    this.setBarColor(idx1, this.colors.default);
    this.setBarColor(idx2, this.colors.default);
    this.highlightLayer.removeMesh(bar1.mesh);
    this.highlightLayer.removeMesh(bar2.mesh);
    this.highlightLayer.removeMesh(bar1.topSphere);
    this.highlightLayer.removeMesh(bar2.topSphere);
  }

  /**
   * 动画移动整个柱子组件
   */
  private animateBarSwap(bar: BarElement, targetX: number, duration: number, goUp: boolean, distance: number): Promise<void> {
    const sphereHeight = bar.topSphere.position.y - bar.mesh.position.y;
    
    // 基本组件动画
    const animations: Promise<void>[] = [
      this.animateFlySwap(bar.mesh, targetX, duration, goUp, distance),
      this.animateSphereFollow(bar.topSphere, targetX, duration, goUp, distance, sphereHeight, bar.mesh.position.y),
      this.animateMeshX(bar.baseTorus, targetX, duration)
    ];
    
    // 额外网格动画（漩涡环、塔层组等）
    if (bar.extraMeshes) {
      for (const extraMesh of bar.extraMeshes) {
        // 检查是否是有子网格的组（如towerGroup、helixGroup）
        const hasChildren = extraMesh.getChildMeshes && extraMesh.getChildMeshes().length > 0;
        
        if (hasChildren) {
          // 对于有子网格的组，只移动父节点，子节点自动跟随
          animations.push(this.animateMeshX(extraMesh, targetX, duration));
        } else {
          // 对于独立的网格（如vortex的rings），直接移动
          animations.push(this.animateMeshX(extraMesh, targetX, duration));
        }
      }
    }
    
    return Promise.all(animations).then(() => {});
  }

  /**
   * 顶部球体跟随动画
   */
  private animateSphereFollow(mesh: Mesh, targetX: number, duration: number, goUp: boolean, distance: number, heightOffset: number, baseY: number): Promise<void> {
    return new Promise(resolve => {
      const fps = 60;
      const frames = Math.round(duration / 16.67);
      const startX = mesh.position.x;
      const startY = mesh.position.y;
      
      const arcHeight = goUp ? Math.max(2.5, distance * 0.8) : -0.3;
      const peakY = startY + arcHeight;
      
      // X位置动画
      const animX = new Animation('moveX', 'position.x', fps, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
      const easeFunc = new CircleEase();
      easeFunc.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);
      animX.setEasingFunction(easeFunc);
      animX.setKeys([
        { frame: 0, value: startX },
        { frame: frames, value: targetX }
      ]);
      
      // Y位置 - 跟随主体的弧线
      const animY = new Animation('moveY', 'position.y', fps, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
      if (goUp) {
        animY.setKeys([
          { frame: 0, value: startY },
          { frame: frames * 0.15, value: startY + arcHeight * 0.5 },
          { frame: frames * 0.35, value: peakY },
          { frame: frames * 0.5, value: peakY },
          { frame: frames * 0.65, value: peakY },
          { frame: frames * 0.85, value: startY + arcHeight * 0.5 },
          { frame: frames, value: startY }
        ]);
      } else {
        animY.setKeys([
          { frame: 0, value: startY },
          { frame: frames * 0.5, value: startY + arcHeight },
          { frame: frames, value: startY }
        ]);
      }
      
      mesh.animations = [animX, animY];
      this.scene.beginAnimation(mesh, 0, frames, false, 1, resolve);
    });
  }

  /**
   * 简单X位置动画
   */
  private animateMeshX(mesh: Mesh, targetX: number, duration: number): Promise<void> {
    return new Promise(resolve => {
      const fps = 60;
      const frames = Math.round(duration / 16.67);
      const startX = mesh.position.x;
      
      const animX = new Animation('moveX', 'position.x', fps, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
      const easeFunc = new CircleEase();
      easeFunc.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);
      animX.setEasingFunction(easeFunc);
      animX.setKeys([
        { frame: 0, value: startX },
        { frame: frames, value: targetX }
      ]);
      
      mesh.animations = [animX];
      this.scene.beginAnimation(mesh, 0, frames, false, 1, resolve);
    });
  }

  /**
   * 标记元素为已排序 - 增强版
   */
  async markSorted(indices: number[], duration = 300): Promise<void> {
    for (const idx of indices) {
      if (this.bars[idx]) {
        this.setBarColor(idx, this.colors.sorted);
        this.highlightLayer.addMesh(this.bars[idx].mesh, Color3.Green());
        
        // 缩放跳动 + 旋转庆祝效果
        await this.animateCelebrate(this.bars[idx].mesh);
      }
    }
    await this.delay(duration);
  }

  /**
   * 重置到初始状态
   */
  async reset(): Promise<void> {
    await this.createFromArray(this.originalArray);
  }

  /**
   * 清除所有元素
   */
  clear(): void {
    for (const bar of this.bars) {
      bar.label.dispose();
      bar.topSphere.dispose();
      bar.baseTorus.dispose();
      bar.mesh.dispose();
      // 清理额外的网格（漩涡光环、螺旋球体组、塔层等）
      if (bar.extraMeshes) {
        for (const m of bar.extraMeshes) {
          // 先清理子网格
          if (m.getChildMeshes) {
            for (const child of m.getChildMeshes()) {
              child.dispose();
            }
          }
          m.dispose();
        }
      }
    }
    this.bars = [];
    this.highlightLayer.removeAllMeshes();
  }

  /**
   * 设置柱子颜色 - 同时更新所有部件
   */
  private setBarColor(index: number, color: Color3): void {
    const bar = this.bars[index];
    if (!bar) return;
    
    // 更新主体圆柱
    if (bar.mesh.material instanceof StandardMaterial) {
      bar.mesh.material.diffuseColor = color;
      bar.mesh.material.emissiveColor = color.scale(0.15);
    }
    
    // 更新顶部球体
    if (bar.topSphere.material instanceof StandardMaterial) {
      bar.topSphere.material.diffuseColor = color;
      bar.topSphere.material.emissiveColor = color.scale(0.5);
    }
    
    // 更新底座
    if (bar.baseTorus.material instanceof StandardMaterial) {
      bar.baseTorus.material.diffuseColor = color.scale(0.6);
      bar.baseTorus.material.emissiveColor = color.scale(0.2);
    }
  }

  /**
   * 位置动画
   */
  private animatePosition(mesh: Mesh, targetX: number, duration: number): Promise<void> {
    return new Promise(resolve => {
      const frames = Math.round(duration / 16.67);
      
      const animation = new Animation(
        'moveX',
        'position.x',
        60,
        Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CONSTANT
      );
      
      // 添加弧线轨迹（上升后下降）
      const startX = mesh.position.x;
      const midFrame = frames / 2;
      
      animation.setKeys([
        { frame: 0, value: startX },
        { frame: frames, value: targetX }
      ]);
      
      // Y轴弧线动画
      const arcAnimation = new Animation(
        'arc',
        'position.y',
        60,
        Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CONSTANT
      );
      
      const startY = mesh.position.y;
      const peakY = startY + 1.5;
      
      arcAnimation.setKeys([
        { frame: 0, value: startY },
        { frame: midFrame, value: peakY },
        { frame: frames, value: startY }
      ]);
      
      mesh.animations = [animation, arcAnimation];
      this.scene.beginAnimation(mesh, 0, frames, false, 1, resolve);
    });
  }

  /**
   * 飞越交换动画 - 超炉酷版
   */
  private animateFlySwap(mesh: Mesh, targetX: number, duration: number, goUp: boolean, distance: number): Promise<void> {
    return new Promise(resolve => {
      const fps = 60;
      const frames = Math.round(duration / 16.67);
      const startX = mesh.position.x;
      const startY = mesh.position.y;
      
      // 计算弧线高度，距离越大弧线越高
      const arcHeight = goUp ? Math.max(2.5, distance * 0.8) : -0.3;
      const peakY = startY + arcHeight;
      
      // X位置动画 - 使用缓动曲线
      const animX = new Animation('flyX', 'position.x', fps, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
      const easeFunc = new CircleEase();
      easeFunc.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);
      animX.setEasingFunction(easeFunc);
      animX.setKeys([
        { frame: 0, value: startX },
        { frame: frames, value: targetX }
      ]);
      
      // Y位置 - 平滑的弧线（多个关键帧）
      const animY = new Animation('flyY', 'position.y', fps, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
      if (goUp) {
        // 向上飞越的平滑弧线
        animY.setKeys([
          { frame: 0, value: startY },
          { frame: frames * 0.15, value: startY + arcHeight * 0.5 },
          { frame: frames * 0.35, value: peakY },
          { frame: frames * 0.5, value: peakY },
          { frame: frames * 0.65, value: peakY },
          { frame: frames * 0.85, value: startY + arcHeight * 0.5 },
          { frame: frames, value: startY }
        ]);
      } else {
        // 向下穿越
        animY.setKeys([
          { frame: 0, value: startY },
          { frame: frames * 0.3, value: startY - 0.5 },
          { frame: frames * 0.5, value: startY - 0.3 },
          { frame: frames * 0.7, value: startY - 0.5 },
          { frame: frames, value: startY }
        ]);
      }
      
      // Z位置 - 向前/向后移动，创造空间感
      const animZ = new Animation('flyZ', 'position.z', fps, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
      const zOffset = goUp ? 1.5 : -1.5;
      animZ.setKeys([
        { frame: 0, value: 0 },
        { frame: frames * 0.3, value: zOffset },
        { frame: frames * 0.7, value: zOffset },
        { frame: frames, value: 0 }
      ]);
      
      // Y轴旋转 - 360度翻转
      const animRotY = new Animation('flyRotY', 'rotation.y', fps, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
      const rotDir = goUp ? 1 : -1;
      animRotY.setKeys([
        { frame: 0, value: 0 },
        { frame: frames, value: rotDir * Math.PI * 2 }
      ]);
      
      // Z轴旋转 - 倾斜效果
      const animRotZ = new Animation('flyRotZ', 'rotation.z', fps, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
      animRotZ.setKeys([
        { frame: 0, value: 0 },
        { frame: frames * 0.25, value: rotDir * Math.PI / 6 },
        { frame: frames * 0.5, value: 0 },
        { frame: frames * 0.75, value: -rotDir * Math.PI / 6 },
        { frame: frames, value: 0 }
      ]);
      
      // 缩放动画 - 拉伸效果
      const animScale = new Animation('flyScale', 'scaling', fps, Animation.ANIMATIONTYPE_VECTOR3, Animation.ANIMATIONLOOPMODE_CONSTANT);
      if (goUp) {
        animScale.setKeys([
          { frame: 0, value: new Vector3(1, 1, 1) },
          { frame: frames * 0.15, value: new Vector3(0.85, 1.25, 0.85) },
          { frame: frames * 0.35, value: new Vector3(1.2, 1.2, 1.2) },
          { frame: frames * 0.65, value: new Vector3(1.2, 1.2, 1.2) },
          { frame: frames * 0.85, value: new Vector3(0.85, 1.25, 0.85) },
          { frame: frames, value: new Vector3(1, 1, 1) }
        ]);
      } else {
        animScale.setKeys([
          { frame: 0, value: new Vector3(1, 1, 1) },
          { frame: frames * 0.3, value: new Vector3(1.1, 0.9, 1.1) },
          { frame: frames * 0.7, value: new Vector3(1.1, 0.9, 1.1) },
          { frame: frames, value: new Vector3(1, 1, 1) }
        ]);
      }
      
      mesh.animations = [animX, animY, animZ, animRotY, animRotZ, animScale];
      this.scene.beginAnimation(mesh, 0, frames, false, 1, resolve);
    });
  }

  /**
   * 着陆弹跳动画
   */
  private animateLanding(mesh: Mesh): Promise<void> {
    return new Promise(resolve => {
      const startY = mesh.position.y;
      
      const animY = new Animation('landY', 'position.y', 60, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
      animY.setKeys([
        { frame: 0, value: startY },
        { frame: 4, value: startY - 0.15 },
        { frame: 8, value: startY + 0.1 },
        { frame: 12, value: startY }
      ]);
      
      const animScale = new Animation('landScale', 'scaling', 60, Animation.ANIMATIONTYPE_VECTOR3, Animation.ANIMATIONLOOPMODE_CONSTANT);
      animScale.setKeys([
        { frame: 0, value: new Vector3(1, 1, 1) },
        { frame: 4, value: new Vector3(1.15, 0.85, 1.15) },
        { frame: 8, value: new Vector3(0.95, 1.08, 0.95) },
        { frame: 12, value: new Vector3(1, 1, 1) }
      ]);
      
      mesh.animations = [animY, animScale];
      this.scene.beginAnimation(mesh, 0, 12, false, 2, resolve);
    });
  }

  /**
   * 交换位置动画 - 带旋转（备用）
   */
  private animateSwapPosition(mesh: Mesh, targetX: number, duration: number, goUp: boolean): Promise<void> {
    return new Promise(resolve => {
      const frames = Math.round(duration / 16.67);
      const midFrame = frames / 2;
      
      // X位置动画
      const animX = new Animation('swapX', 'position.x', 60, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
      const startX = mesh.position.x;
      animX.setKeys([
        { frame: 0, value: startX },
        { frame: frames, value: targetX }
      ]);
      
      // Y位置弧线 - 一个向上一个向下
      const animY = new Animation('swapY', 'position.y', 60, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
      const startY = mesh.position.y;
      const peakY = goUp ? startY + 2.5 : startY - 0.5;
      animY.setKeys([
        { frame: 0, value: startY },
        { frame: midFrame, value: peakY },
        { frame: frames, value: startY }
      ]);
      
      // 旋转动画
      const animRot = new Animation('swapRot', 'rotation.z', 60, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
      const rotDir = goUp ? 1 : -1;
      animRot.setKeys([
        { frame: 0, value: 0 },
        { frame: midFrame, value: rotDir * Math.PI / 8 },
        { frame: frames, value: 0 }
      ]);
      
      // 缩放动画
      const animScale = new Animation('swapScale', 'scaling', 60, Animation.ANIMATIONTYPE_VECTOR3, Animation.ANIMATIONLOOPMODE_CONSTANT);
      animScale.setKeys([
        { frame: 0, value: new Vector3(1, 1, 1) },
        { frame: midFrame, value: new Vector3(1.15, 1.15, 1.15) },
        { frame: frames, value: new Vector3(1, 1, 1) }
      ]);
      
      mesh.animations = [animX, animY, animRot, animScale];
      this.scene.beginAnimation(mesh, 0, frames, false, 1, resolve);
    });
  }

  /**
   * 脉冲动画
   */
  private animatePulse(mesh: Mesh, duration: number): Promise<void> {
    return new Promise(resolve => {
      const frames = Math.round(duration / 16.67);
      
      const animation = new Animation(
        'pulse',
        'scaling',
        60,
        Animation.ANIMATIONTYPE_VECTOR3,
        Animation.ANIMATIONLOOPMODE_CONSTANT
      );
      
      animation.setKeys([
        { frame: 0, value: new Vector3(1, 1, 1) },
        { frame: frames * 0.25, value: new Vector3(1.15, 1.1, 1.15) },
        { frame: frames * 0.5, value: new Vector3(1, 1, 1) },
        { frame: frames * 0.75, value: new Vector3(1.1, 1.05, 1.1) },
        { frame: frames, value: new Vector3(1, 1, 1) }
      ]);
      
      mesh.animations = [animation];
      this.scene.beginAnimation(mesh, 0, frames, false, 1, resolve);
    });
  }

  /**
   * 庆祝动画 - 跳跃+旋转
   */
  private animateCelebrate(mesh: Mesh): Promise<void> {
    return new Promise(resolve => {
      const startY = mesh.position.y;
      
      // 跳跃动画
      const jumpAnim = new Animation('jump', 'position.y', 60, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
      jumpAnim.setKeys([
        { frame: 0, value: startY },
        { frame: 8, value: startY + 1.2 },
        { frame: 16, value: startY }
      ]);
      
      // 缩放动画
      const scaleAnim = new Animation('celebrateScale', 'scaling', 60, Animation.ANIMATIONTYPE_VECTOR3, Animation.ANIMATIONLOOPMODE_CONSTANT);
      scaleAnim.setKeys([
        { frame: 0, value: new Vector3(1, 1, 1) },
        { frame: 4, value: new Vector3(1.2, 0.85, 1.2) },
        { frame: 8, value: new Vector3(0.9, 1.2, 0.9) },
        { frame: 12, value: new Vector3(1.05, 0.95, 1.05) },
        { frame: 16, value: new Vector3(1, 1, 1) }
      ]);
      
      // 旋转动画
      const rotAnim = new Animation('celebrateRot', 'rotation.y', 60, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
      rotAnim.setKeys([
        { frame: 0, value: 0 },
        { frame: 16, value: Math.PI * 2 }
      ]);
      
      mesh.animations = [jumpAnim, scaleAnim, rotAnim];
      this.scene.beginAnimation(mesh, 0, 16, false, 2, resolve);
    });
  }

  /**
   * 延迟函数
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * 获取当前数组值
   */
  getValues(): number[] {
    return this.bars.map(bar => bar.value);
  }

  /**
   * 获取元素数量
   */
  getLength(): number {
    return this.bars.length;
  }
}
